import { computeSpaces, spaceContains } from './spaces.jsx';
import { expandArcs } from './arcWalls.jsx';

// «مساحتِ اتاق»: پلان مثلِ یک شبکه‌ی کش‌آمدنی دیده می‌شود — همه‌ی سرِ دیوارها روی چند خطِ عمودی (x) و افقی (y)
// هستند و فاصله‌ی دو خطِ کنارِ هم = یک «باند». مساحتِ هر فضا = جمعِ خانه‌های شبکه‌ی داخلش (محور تا محورِ دیوار).
// عوض کردنِ مساحتِ یک اتاق = عوض کردنِ پهنای چند باند؛ با این کار:
//   • اتاق‌هایی که کاربر خودش مساحتشان را داده (locked) ثابت می‌مانند،
//   • قابِ بیرونیِ خانه (پس متراژِ کل) ثابت می‌ماند → اتاق‌های تخمینی کوچک/بزرگ می‌شوند،
//   • تغییر تا جای ممکن کم و پخش است (کمترین مربعاتِ وزن‌دار، باندِ کوچک کمتر عوض می‌شود).
//   • متراژِ کلِ فضاها هم ثابت می‌ماند (خانه‌ی L شکل: تکه‌ی بیرونی نباید جای اتاق را بگیرد).
// اگر ممکن نبود: اول فقط متراژِ کل ثابت، بعد هیچ‌کدام (مثلاً وقتی همه‌ی اتاق‌های آن راستا ثابت‌اند).

const KEY = (v) => Math.round(v * 2) / 2;
const uniq = (arr) => [...new Set(arr.map(KEY))].sort((a, b) => a - b);

// مدل: خطوط، باندها و اینکه هر خانه مالِ کدام فضاست
export function buildAreaModel(walls, spaces = null) {
  const valid = (expandArcs(walls) || []).filter((w) => Math.hypot(w.x2 - w.x1, w.y2 - w.y1) > 1);
  if (!valid.length) return null;
  const sp = spaces || computeSpaces(valid, [], [], []);
  const xs = uniq(valid.flatMap((w) => [w.x1, w.x2]));
  const ys = uniq(valid.flatMap((w) => [w.y1, w.y2]));
  if (xs.length < 2 || ys.length < 2) return null;
  const gx = xs.slice(1).map((v, i) => v - xs[i]);
  const gy = ys.slice(1).map((v, i) => v - ys[i]);
  const cell = new Int32Array(gx.length * gy.length).fill(-1);
  for (let j = 0; j < gy.length; j += 1) {
    const cy = (ys[j] + ys[j + 1]) / 2;
    for (let i = 0; i < gx.length; i += 1) {
      const cx = (xs[i] + xs[i + 1]) / 2;
      cell[j * gx.length + i] = sp.findIndex((s) => spaceContains(s, cx, cy));
    }
  }
  return { xs, ys, gx, gy, cell, spaces: sp };
}

// مساحتِ هر فضا (سانتی‌متر مربع) با باندهای داده‌شده
export function spaceAreas(model, gx = model.gx, gy = model.gy) {
  const out = new Array(model.spaces.length).fill(0);
  for (let j = 0; j < gy.length; j += 1) {
    for (let i = 0; i < gx.length; i += 1) {
      const s = model.cell[j * gx.length + i];
      if (s >= 0) out[s] += gx[i] * gy[j];
    }
  }
  return out;
}

export const spaceAt = (model, x, y) => (model ? model.spaces.findIndex((s) => spaceContains(s, x, y)) : -1);

// حلِ دستگاهِ خطی کوچک (حذفِ گاوسی با محور‌گیری). null اگر منفرد بود
function solveLinear(A, b) {
  const n = b.length;
  const M = A.map((row, i) => [...row, b[i]]);
  for (let c = 0; c < n; c += 1) {
    let p = c;
    for (let r = c + 1; r < n; r += 1) if (Math.abs(M[r][c]) > Math.abs(M[p][c])) p = r;
    if (Math.abs(M[p][c]) < 1e-12) return null;
    [M[c], M[p]] = [M[p], M[c]];
    for (let r = 0; r < n; r += 1) {
      if (r === c) continue;
      const f = M[r][c] / M[c][c];
      if (f) for (let k = c; k <= n; k += 1) M[r][k] -= f * M[c][k];
    }
  }
  return M.map((row, i) => row[n] / row[i]);
}

// یک راستا: g (باندهای این راستا) عوض می‌شود، باندهای راستای دیگر ثابت. cons: [{ s, area }] (s = −2 یعنی «همه‌ی
// فضاها با هم» = متراژِ کل). کمینه‌ی Σ w(g−g0)² با قیدهای خطیِ Σ c·g = area
// mode: 'both' = قاب و متراژِ کل ثابت | 'area' = فقط متراژِ کل ثابت | 'none' = هیچ‌کدام
function solveAxis(model, axis, cons, mode) {
  const g0 = axis === 'x' ? model.gx : model.gy;
  const other = axis === 'x' ? model.gy : model.gx;
  const n = g0.length;
  const nx = model.gx.length;
  const rows = [];
  const rhs = [];
  cons.forEach(({ s, area }) => {
    const c = new Array(n).fill(0);
    for (let j = 0; j < model.gy.length; j += 1) {
      for (let i = 0; i < nx; i += 1) {
        const cs = model.cell[j * nx + i];
        if (s === -2 ? cs < 0 : cs !== s) continue;
        if (axis === 'x') c[i] += other[j];
        else c[j] += other[i];
      }
    }
    rows.push(c);
    rhs.push(area);
  });
  if (mode === 'both') {
    rows.push(new Array(n).fill(1));
    rhs.push(g0.reduce((a, v) => a + v, 0));
  }
  const lb = g0.map((v) => Math.min(v, 60) * 0.5);
  const fixed = new Map(); // باندی که به کفِ مجاز رسید
  const w = g0.map((v) => 1 / Math.max(v, 10));
  for (let iter = 0; iter < n + 2; iter += 1) {
    // قیدها روی باندهای آزاد
    const free = [];
    for (let i = 0; i < n; i += 1) if (!fixed.has(i)) free.push(i);
    const R = rows.map((r) => free.map((i) => r[i]));
    const b = rows.map((r, k) => {
      let v = rhs[k];
      fixed.forEach((val, i) => {
        v -= r[i] * val;
      });
      free.forEach((i, fi) => {
        v -= R[k][fi] * g0[i];
      });
      return v;
    });
    // (R W⁻¹ Rᵀ + εI) λ = b
    const m = R.length;
    const A = Array.from({ length: m }, (_, a) =>
      Array.from({ length: m }, (__, c) => {
        let v = 0;
        free.forEach((i, fi) => {
          v += (R[a][fi] * R[c][fi]) / w[i];
        });
        return v + (a === c ? 1e-9 * (1 + Math.abs(v)) : 0);
      })
    );
    const lam = m ? solveLinear(A, b) : [];
    if (!lam) return null;
    const g = g0.slice();
    fixed.forEach((val, i) => {
      g[i] = val;
    });
    free.forEach((i, fi) => {
      let d = 0;
      for (let k = 0; k < m; k += 1) d += R[k][fi] * lam[k];
      g[i] = g0[i] + d / w[i];
    });
    const bad = free.filter((i) => g[i] < lb[i] - 1e-6);
    if (!bad.length) {
      // قیدها واقعاً برقرارند؟ (ناسازگاری بینِ قیدها)
      for (let k = 0; k < rows.length; k += 1) {
        const got = rows[k].reduce((a, c, i) => a + c * g[i], 0);
        if (Math.abs(got - rhs[k]) > Math.max(1, Math.abs(rhs[k]) * 0.004)) return null;
      }
      const cost = g.reduce((a, v, i) => a + w[i] * (v - g0[i]) ** 2, 0);
      return { g, cost };
    }
    bad.forEach((i) => fixed.set(i, lb[i]));
  }
  return null;
}

const withAxis = (model, axis, g) => ({ ...model, [axis === 'x' ? 'gx' : 'gy']: g });

// هدف: مساحتِ فضای target به areaCm2؛ locked: [{ s, area }] فضاهایی که باید ثابت بمانند
// خروجی: { gx, gy, frameChanged } یا null (ناممکن)
export function solveRoomArea(model, target, areaCm2, locked = []) {
  if (!model || target < 0) return null;
  const others = locked.filter((c) => c.s !== target);
  const cur = spaceAreas(model)[target];
  if (!cur) return null;
  const tries = [];
  const totalNow = spaceAreas(model).reduce((a, v) => a + v, 0);
  const attempt = (mode) => {
    // متراژِ کلِ خانه ثابت (اتاق‌های تخمینی جا باز می‌کنند)، مگر در حالتِ 'none'
    const tot = mode === 'none' ? [] : [{ s: -2, area: totalNow }];
    const keepTotal = mode;
    const full = [{ s: target, area: areaCm2 }, ...others, ...tot];
    const x = solveAxis(model, 'x', full, keepTotal);
    if (x) tries.push({ gx: x.g, gy: model.gy, cost: x.cost, frameChanged: mode !== 'both' });
    const y = solveAxis(model, 'y', full, keepTotal);
    if (y) tries.push({ gx: model.gx, gy: y.g, cost: y.cost, frameChanged: mode !== 'both' });
    // نیمی از تغییر در یک راستا، بقیه در راستای دیگر (اتاق شکلش را بهتر نگه می‌دارد)
    const half = Math.sqrt(cur * areaCm2);
    ['x', 'y'].forEach((a1) => {
      const a2 = a1 === 'x' ? 'y' : 'x';
      // قیدِ اتاق‌های ثابت در گامِ اول هم باید برقرار بماند
      const s1 = solveAxis(model, a1, [{ s: target, area: half }, ...others, ...tot], keepTotal);
      if (!s1) return;
      const m1 = withAxis(model, a1, s1.g);
      const s2 = solveAxis(m1, a2, full, keepTotal);
      if (!s2) return;
      const m2 = withAxis(m1, a2, s2.g);
      // هزینه‌ی کمی کمتر — همین را ترجیح می‌دهیم وقتی ممکن است
      tries.push({ gx: m2.gx, gy: m2.gy, cost: (s1.cost + s2.cost) * 0.8, frameChanged: mode !== 'both' });
    });
  };
  attempt('both');
  if (!tries.length) attempt('area');
  if (!tries.length) attempt('none');
  if (!tries.length) return null;
  tries.sort((a, b) => a.cost - b.cost);
  return tries[0];
}

// نگاشتِ یک مختصات از خطوطِ قدیم به جدید (درونِ هر باند خطی؛ بیرون از شبکه: جابه‌جاییِ لبه)
function mapper(lines, gaps) {
  const next = [lines[0]];
  gaps.forEach((g, i) => next.push(next[i] + g));
  return (v) => {
    if (v <= lines[0]) return v + (next[0] - lines[0]);
    const last = lines.length - 1;
    if (v >= lines[last]) return v + (next[last] - lines[last]);
    let k = 0;
    while (k < last - 1 && v > lines[k + 1]) k += 1;
    const t = (v - lines[k]) / (lines[k + 1] - lines[k] || 1);
    return next[k] + (next[k + 1] - next[k]) * t;
  };
}

// تابعِ نگاشتِ نقطه { x, y } → { x, y } (گرد شده به سانتی‌متر)
export function makePointMap(model, sol) {
  const fx = mapper(model.xs, sol.gx);
  const fy = mapper(model.ys, sol.gy);
  return (x, y) => ({ x: Math.round(fx(x)), y: Math.round(fy(y)) });
}
